import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { api } from "../lib/api"
import type { Role } from "@shared/types"

interface AuthUser {
  id: string
  email: string
  role: Role
  status: string
  lastLoginAt?: number
}

interface AdminProfile {
  id: string
  fullName: string
  displayName?: string
  permissions: string[]
}

interface AuthContextValue {
  user: AuthUser | null
  adminProfile: AdminProfile | null
  loading: boolean
  login: (email: string, password: string) => Promise<{ role: Role }>
  register: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [adminProfile, setAdminProfile] = useState<AdminProfile | null>(null)
  const [loading, setLoading] = useState(true)

  async function refresh() {
    try {
      const res = await api.get<{ user: AuthUser | null; profile?: AdminProfile }>("/api/auth/me")
      setUser(res.user)
      if (res.profile) {
        setAdminProfile({
          ...res.profile,
          permissions: typeof res.profile.permissions === "string"
            ? JSON.parse(res.profile.permissions)
            : (res.profile.permissions || []),
        })
      } else {
        setAdminProfile(null)
      }
    } catch {
      setUser(null)
      setAdminProfile(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [])

  async function login(email: string, password: string) {
    const res = await api.post<{ success: boolean; role: Role }>("/api/auth/login", { email, password })
    await refresh()
    return { role: res.role }
  }

  async function register(email: string, password: string) {
    await api.post("/api/auth/register", { email, password })
    await refresh()
  }

  async function logout() {
    try { await api.post("/api/auth/logout") } catch {}
    setUser(null)
    setAdminProfile(null)
  }

  return (
    <AuthContext.Provider value={{ user, adminProfile, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider")
  return ctx
}
